import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Coins, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../store';

const API_URL = process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? ((window.location.hostname === 'devapp.soviewfx.fr' || window.location.hostname === 'devadmin.soviewfx.fr') ? 'https://devhub.soviewfx.fr' : ((window.location.hostname === 'app.soviewfx.fr' || window.location.hostname === 'admin.soviewfx.fr') ? 'https://hub.soviewfx.fr' : '')) : '');

export default function SubscriptionModalV2({ onClose }) {
  const { user, token } = useAuthStore();

  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    fetchPlans();
    if (token) fetchSubscription();
  }, [token]);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data);
        if (data.length > 0) setSelectedPlan(data[0].id);
      } else {
        // Fallback hardcoded plans
        const fallback = [
          { id: 'monthly', name: 'Mensuel', price: 29.90, credits: 50, period: '/mois' },
          { id: 'annual', name: 'Annuel', price: 249, credits: 600, period: '/an', badge: '-30%' }
        ];
        setPlans(fallback);
        setSelectedPlan('monthly');
      }
    } catch {
      const fallback = [
        { id: 'monthly', name: 'Mensuel', price: 29.90, credits: 50, period: '/mois' },
        { id: 'annual', name: 'Annuel', price: 249, credits: 600, period: '/an', badge: '-30%' }
      ];
      setPlans(fallback);
      setSelectedPlan('monthly');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/api/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscription(data);
      }
    } catch {
      // Not subscribed — fine
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user || !token) {
      toast.error('Veuillez vous connecter');
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          price_id: planId,
          success_url: window.location.origin + '/payment/success',
          cancel_url: window.location.origin + '/payment/cancel'
        })
      });

      if (!res.ok) throw new Error('Échec de la création de la session de paiement');
      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error('Échec de la résiliation');
      toast.success('Abonnement résilié');
      setSubscription(null);
      setConfirmCancel(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const currentPlanId = subscription?.plan_id;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative glass-light rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg hover:bg-white/10"
        >
          <X size={20} className="text-muted-foreground" />
        </button>

        <h2 className="text-xl font-bold text-white mb-2">Abonnement</h2>
        <p className="text-sm text-muted-foreground mb-6">
          {subscription
            ? 'Gérez votre abonnement actuel'
            : 'Choisissez le plan qui vous convient'}
        </p>

        {/* Current credits */}
        {user && (
          <div className="flex items-center gap-2 mb-4 text-primary">
            <Coins size={18} />
            <span className="font-bold">{user.credits ?? 0}</span>
            <span className="text-sm text-muted-foreground">crédits restants</span>
          </div>
        )}

        {/* Current subscription info */}
        {subscription && (
          <div className="mb-4 p-3 rounded-xl bg-secondary/10 border border-secondary/30">
            <p className="text-sm font-semibold text-secondary">
              Plan actuel : {subscription.plan_name || currentPlanId}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {subscription.status === 'active' ? 'Actif' : subscription.status}
              {subscription.renewal_date && ` — Prochain renouvellement : ${new Date(subscription.renewal_date).toLocaleDateString('fr-FR')}`}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={32} className="text-secondary animate-spin" />
          </div>
        ) : (
          <>
            {/* Plans */}
            <div className="space-y-3 mb-6">
              {plans.map((plan) => {
                const isCurrent = plan.id === currentPlanId;
                const isSelected = selectedPlan === plan.id;

                return (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    disabled={isCurrent}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all relative ${
                      isCurrent
                        ? 'border-secondary bg-secondary/10 opacity-80'
                        : isSelected
                          ? 'border-secondary bg-secondary/10'
                          : 'border-white/10 bg-background hover:border-white/20'
                    }`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.badge && !isCurrent && (
                      <span className="absolute top-2 right-2 bg-secondary text-background text-xs font-bold px-2 py-0.5 rounded-full">
                        {plan.badge}
                      </span>
                    )}
                    {isCurrent && (
                      <span className="absolute top-2 right-2 bg-primary text-background text-xs font-bold px-2 py-0.5 rounded-full">
                        Actuel
                      </span>
                    )}
                    <p className="font-bold text-white">{plan.name}</p>
                    <p className="text-2xl font-bold text-secondary mt-1">
                      {plan.price}€
                      <span className="text-sm text-muted-foreground font-normal">{plan.period}</span>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {plan.credits} crédits IA
                    </p>
                    {(isSelected || isCurrent) && (
                      <Check size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            {confirmCancel ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30">
                  <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-red-400">Confirmer la résiliation</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Vous perdrez l'accès aux fonctionnalités payantes à la fin de la période en cours.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setConfirmCancel(false)}
                    className="flex-1 py-3 rounded-xl bg-white/5 border border-white/10 text-muted-foreground font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={actionLoading}
                    className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500 text-red-400 font-bold disabled:opacity-50"
                  >
                    {actionLoading ? '...' : 'Confirmer la résiliation'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {currentPlanId && selectedPlan !== currentPlanId ? (
                  <button
                    onClick={() => handleSubscribe(selectedPlan)}
                    disabled={actionLoading || !selectedPlan}
                    className="w-full btn-primary disabled:opacity-50"
                    data-testid="change-plan-btn"
                  >
                    {actionLoading
                      ? 'Redirection...'
                      : `Changer pour ${
                          plans.find((p) => p.id === selectedPlan)?.price
                        }€`}
                  </button>
                ) : !currentPlanId ? (
                  <button
                    onClick={() => handleSubscribe(selectedPlan)}
                    disabled={actionLoading || !selectedPlan}
                    className="w-full btn-primary disabled:opacity-50"
                    data-testid="subscribe-btn"
                  >
                    {actionLoading
                      ? 'Redirection...'
                      : `Souscrire - ${
                          plans.find((p) => p.id === selectedPlan)?.price
                        }€`}
                  </button>
                ) : null}

                {currentPlanId && (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    disabled={actionLoading}
                    className="w-full py-3 rounded-xl bg-white/5 border border-white/10 text-red-400 font-medium hover:bg-red-500/10 transition-colors"
                  >
                    Résilier l'abonnement
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
}
